import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  CheckCircle,
  Warning,
  ArrowLeft
} from '@phosphor-icons/react';


export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, {
        withCredentials: true
      });
      
      setPaymentInfo(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <CircleNotch size={64} className="animate-spin text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verificando pago...</h1>
            <p className="text-slate-500">Por favor espera mientras confirmamos tu pago.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} className="text-green-600" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">¡Pago Exitoso!</h1>
            <p className="text-slate-500 mb-6">
              Tu pago de ${(paymentInfo?.amount_total / 100)?.toLocaleString()} ha sido procesado correctamente.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <ArrowLeft size={18} />
              Volver al Dashboard
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning size={48} className="text-red-600" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Error</h1>
            <p className="text-slate-500 mb-6">
              No pudimos verificar tu pago. Por favor contacta a soporte.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium"
            >
              <ArrowLeft size={18} />
              Volver al Dashboard
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning size={48} className="text-yellow-600" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Sesión Expirada</h1>
            <p className="text-slate-500 mb-6">
              La sesión de pago ha expirado. Por favor intenta nuevamente.
            </p>
            <Link
              to="/quotes"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <ArrowLeft size={18} />
              Ver Cotizaciones
            </Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning size={48} className="text-yellow-600" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verificación en progreso</h1>
            <p className="text-slate-500 mb-6">
              La verificación está tomando más tiempo de lo esperado. Recibirás una confirmación por email.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <ArrowLeft size={18} />
              Volver al Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
